import { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Check } from "lucide-react";

const IMG = "https://images.unsplash.com/photo-1600818797017-d6e5027210bb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwxfHxjdXAlMjBvZiUyMHRlYSUyMGFuZCUyMG9wZW4lMjBib29rJTIwbWluaW1hbGlzdHxlbnwwfHx8fDE3ODkyMDYxMTV8MA&ixlib=rb-4.1.0&q=85";

const themes = [
  {
    week: 1,
    title: "L'ado que j'étais",
    excerpt: "Ce que penserait de moi, de ma vie, l'adolescente que j'étais...",
  },
  {
    week: 5,
    title: "Une odeur qui convoque un souvenir",
    excerpt: "Cette odeur, pour moi, c'est l'odeur un peu humide de la terre battue...",
  },
  {
    week: 32,
    title: "Une lettre à qui vivra dans 100 ans",
    excerpt: "À toi qui ne connaîtras jamais rien de moi que ces quelques mots, je voudrais te dire d'être indulgente avec toi-même, d'être patiente et d'écouter tous ces petits signaux que ton corps t'envoie...",
  },
];

export default function Programme() {
  const [loading, setLoading] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [code, setCode] = useState("");

  async function checkout(lookup_key) {
    if (!email) return toast.error("Merci d'indiquer ton email");
    setLoading(lookup_key);
    try {
      const r = await api.post("/payments/checkout", {
        lookup_key, origin_url: window.location.origin, email, name,
        code: code || undefined,
      });
      if (r.data.free_order) { window.location.href = r.data.redirect_url; return; }
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
      setLoading("");
    }
  }

  return (
    <div data-testid="programme-page">
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-16 pb-16 grid md:grid-cols-12 gap-10 items-center fade-in">
        <div className="md:col-span-6">
          <p className="font-hand text-2xl text-[#A3876A] mb-4">Une année de rendez-vous avec toi-même</p>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl mb-6 leading-tight">
            Entre dans une année <em className="italic">d'écriture</em>
          </h1>
          <p className="text-lg text-[#4A4036] leading-relaxed mb-6">
            Et si, pendant un an, tu décidais de renouer avec le temps de la plus belle des façons — en écrivant ?
          </p>
          <p className="text-[#4A4036] leading-relaxed max-w-xl italic">
            52 semaines. 52 thématiques. 52 rendez-vous pour t'arrêter, te retrouver, et mettre des mots sur ce qui te traverse. Une invitation à sortir de la frénésie de la vie, une fois par semaine.
          </p>
        </div>
        <div className="md:col-span-6">
          <img src={IMG} alt="Livre ouvert et tasse de thé" className="w-full h-[440px] object-cover"/>
        </div>
      </section>

      {/* Structure narrative — Pour qui / Ce que tu vivras / Ce que tu reçois */}
      <section className="bg-[#F5F0E6] py-20 border-y border-[#E8DCC8]">
        <div className="max-w-6xl mx-auto px-6 md:px-12 grid md:grid-cols-3 gap-10">
          <div>
            <p className="font-hand text-xl text-[#A3876A] mb-3">À qui ça s'adresse</p>
            <h3 className="font-serif text-2xl mb-4">Pour toi qui…</h3>
            <ul className="space-y-2 text-[#4A4036] leading-relaxed">
              <li>· aimerais écrire davantage sans savoir par où commencer</li>
              <li>· cherches une pause régulière rien qu'à toi</li>
              <li>· veux garder une trace de ce que tu vis</li>
              <li>· crois « ne pas savoir écrire » — et te trompes</li>
            </ul>
          </div>
          <div>
            <p className="font-hand text-xl text-[#A3876A] mb-3">Ce que tu vas vivre</p>
            <h3 className="font-serif text-2xl mb-4">Une invitation, chaque semaine</h3>
            <p className="text-[#4A4036] leading-relaxed">
              Chaque semaine, un nouveau rendez-vous arrive dans ta boîte mail. Une thématique douce, un extrait de mon carnet — je me suis moi aussi prêtée à l'exercice — puis une invitation à écrire à ton tour. Aucune attente, pas besoin de savoir bien écrire ni de produire quelque chose de beau. Simplement laisser les mots se déposer.
            </p>
          </div>
          <div>
            <p className="font-hand text-xl text-[#A3876A] mb-3">Ce que tu explores</p>
            <h3 className="font-serif text-2xl mb-4">Une année pour te retrouver</h3>
            <p className="text-[#4A4036] leading-relaxed">
              Semaine après semaine, tu explores qui tu es, ce que tu ressens, ce dont tu rêves, ce que tu as perdu, les différentes versions de toi… d'une manière douce et inédite.
            </p>
          </div>
        </div>
      </section>

      {/* Aperçu des thèmes */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <h2 className="font-serif text-3xl md:text-4xl mb-3 text-center">Un aperçu des thèmes</h2>
          <p className="text-center text-[#7C7064] mb-12 italic">Trois parmi les cinquante-deux</p>
          <div className="grid md:grid-cols-3 gap-4">
            {themes.map((t, i) => (
              <div key={t.week} className="bg-[#FDFBF7] p-6 border border-[#E8DCC8] flex flex-col">
                <p className="font-hand text-xl text-[#A3876A]">Semaine {t.week}</p>
                <p className="font-serif text-xl mt-1 mb-3">{t.title}</p>
                <p className="text-sm text-[#4A4036] italic leading-relaxed">« {t.excerpt} »</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comment ça se passe */}
      <section className="max-w-4xl mx-auto px-6 md:px-12 py-20">
        <div className="grid md:grid-cols-2 gap-10">
          <div>
            <p className="font-hand text-2xl text-[#A3876A] mb-3">Comment ça se passe</p>
            <h2 className="font-serif text-3xl md:text-4xl leading-tight">Tu es libre, à chaque rendez-vous.</h2>
          </div>
          <div className="space-y-6 text-[#4A4036] leading-relaxed">
            <p>
              <strong className="text-[#2C2621]">Envie d'être lue ?</strong> Réponds directement à l'email. Je prends le temps de lire chaque réponse, à mon rythme. Ce n'est pas un service de correction — c'est simplement, si tu le souhaites, une paire d'yeux bienveillants sur tes mots.
            </p>
            <p>
              <strong className="text-[#2C2621]">Envie d'écrire juste pour toi ?</strong> C'est parfait aussi. Prends un carnet, ton application préférée, un carré de papier trouvé sur un coin de bureau. Le rendez-vous est à toi, tu en fais ce que tu veux.
            </p>
            <p className="font-hand text-xl text-[#A3876A]">
              Pas de contrainte. Pas de jugement. Juste un rendez-vous, chaque semaine, avec toi-même.
            </p>
          </div>
        </div>
      </section>

      {/* Formules */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 py-20 border-t border-[#E8DCC8]">
        <div className="text-center mb-4">
          <p className="font-hand text-2xl text-[#A3876A]">Le tarif</p>
        </div>
        <h2 className="font-serif text-3xl md:text-4xl text-center mb-10">Choisis ta version</h2>

        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <input data-testid="programme-name-input" className="field-input" placeholder="Ton prénom" value={name} onChange={e=>setName(e.target.value)}/>
          <input data-testid="programme-email-input" className="field-input" type="email" placeholder="Ton email" value={email} onChange={e=>setEmail(e.target.value)}/>
        </div>

        <div className="mb-10 max-w-md">
          <label className="text-sm text-[#7C7064] block mb-2">Tu as un code cadeau ?</label>
          <input data-testid="programme-code-input" className="field-input" placeholder="CADEAU-XXXXXXXX" value={code} onChange={e=>setCode(e.target.value.toUpperCase())}/>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {[
            {
              lk:"programme_seul",
              title:"Programme Essentiel",
              price:"102€",
              tag:"un rendez-vous chaque semaine, rien que pour toi",
              features:[
                "52 rendez-vous d'écriture pendant un an",
                "Un thème inspirant chaque semaine",
                "Mes propres réponses à chaque thématique",
                "La possibilité de me répondre pour être lue",
              ],
              cta:"Je m'offre une année d'écriture",
            },
            {
              lk:"programme_coffret",
              title:"Programme Épistolaire",
              price:"152€",
              featured:true,
              tag:"la version qui va au-delà du rendez-vous",
              features:[
                "Tout ce qu'inclut la version Essentielle",
                "Un coffret de bienvenue livré chez toi",
                "Un échange de lettre une fois dans l'année, de celles qu'on est impatient de recevoir",
                "Retrouver le plaisir unique de la correspondance",
              ],
              cta:"Je choisis la version épistolaire",
            },
          ].map((p) => (
            <div key={p.lk} data-testid={`plan-${p.lk}`} className={`p-10 border ${p.featured ? "border-[#A3876A] bg-[#FDFBF7]" : "border-[#E8DCC8] bg-[#F5F0E6]"}`}>
              <h3 className="font-serif text-3xl mb-1">{p.title}</h3>
              <p className="font-hand text-lg text-[#A3876A] mb-4">{p.tag}</p>
              <p className="font-serif text-5xl text-[#2C2621] mb-6">{p.price}</p>
              <ul className="space-y-3 mb-8">
                {p.features.map((f,i) => (
                  <li key={f} className="flex items-start gap-3 text-[#4A4036]"><Check size={16} className="text-[#A3876A] mt-1 flex-shrink-0"/> {f}</li>
                ))}
              </ul>
              <button data-testid={`checkout-${p.lk}`} onClick={() => checkout(p.lk)} disabled={!!loading} className="btn-primary w-full justify-center">
                {loading === p.lk ? "Redirection…" : p.cta}
              </button>
            </div>
          ))}
        </div>

        {/* Signature Amélie — clôture douce */}
        <div className="max-w-2xl mx-auto text-center mt-16 pt-12 border-t border-[#E8DCC8]">
          <p className="text-[#4A4036] leading-relaxed italic mb-6">
            Choisis ta version, clique sur le lien pour commencer ton parcours, et laisse-toi guider.
          </p>
          <p className="font-hand text-3xl text-[#A3876A] mb-1">À bientôt,</p>
          <p className="font-serif text-2xl text-[#2C2621]">Amélie</p>
        </div>
      </section>
    </div>
  );
}
